import React from "react";
import { BookingTag } from "../../../../../../types/VenueGroup";
import { EditableFieldSwitch } from "../../../../../blocks/form-fields";

export type CustomFieldValue = string | boolean | string[] | null;

interface CustomFieldCardProps {
  field: BookingTag;
  value: CustomFieldValue;
  canEdit: boolean;
  isArchived?: boolean;
  onChange: (fieldId: string, value: CustomFieldValue) => void;
}

const ReadOnlyCustomField = ({
  label,
  value,
  isArchived = false
}: {
  label: string;
  value: CustomFieldValue;
  isArchived?: boolean;
}) => {
  const displayValue = (() => {
    if (value === null || value === undefined) return "Not set";
    if (typeof value === "boolean") return value ? "Yes" : "No";
    if (Array.isArray(value))
      return value.length > 0 ? value.join(", ") : "Not set";
    const strValue = String(value).trim();
    return strValue || "Not set";
  })();

  return (
    <div className={`flex flex-col gap-1${isArchived ? " opacity-60" : ""}`}>
      <span className="text-sm font-medium text-foreground">
        {label}
        {isArchived && (
          <span className="ml-1 text-xs font-normal text-muted-foreground">
            (archived)
          </span>
        )}
      </span>
      <span className="text-sm text-muted-foreground">{displayValue}</span>
    </div>
  );
};

const customFieldToRequirementType = (type: string): string => {
  const supported = ["boolean", "text", "longtext", "select", "multiselect"];
  return supported.includes(type) ? type : "";
};

export const CustomFieldCard = React.memo(function CustomFieldCard({
  field,
  value,
  canEdit,
  isArchived = false,
  onChange
}: CustomFieldCardProps) {
  if (!canEdit) {
    return (
      <ReadOnlyCustomField
        label={field.label}
        value={value}
        isArchived={isArchived}
      />
    );
  }

  return (
    <EditableFieldSwitch
      fieldId={`custom-field-${field.id}`}
      type={customFieldToRequirementType(field.type)}
      label={field.label}
      value={value}
      options={field.options}
      onChange={(newValue) => {
        if (typeof newValue === "number") return;
        onChange(field.id, newValue);
      }}
      fallback={<ReadOnlyCustomField label={field.label} value={value} />}
    />
  );
});
